import { getClient } from '../core/global-config.js';
import { SeacloudError } from '../core/types.js';
import { getApiToken, checkAndNotifyInsufficientBalance, checkResponseBodyForInsufficientBalance } from '../core/config.js';

/**
 * 风险类型枚举
 */
export type RiskType = 'POLITY' | 'EROTIC' | 'VIOLENT' | 'CHILD';

/**
 * 标签项
 */
export interface LabelItem {
  /**
   * 标签名称，格式: 场景:类别:具体标签
   */
  name: string;
  /**
   * 标签对应的风险级别 (0-6)
   */
  score: number;
  /**
   * 标签所属的风险类型
   */
  risk_type: RiskType;
}

/**
 * 视频帧检测结果
 */
export interface FrameResult {
  /**
   * 帧索引号
   */
  frame_index: number;
  /**
   * 风险级别
   */
  nsfw_level: number;
  /**
   * 标签列表
   */
  label_items: LabelItem[];
  /**
   * 风险类型列表
   */
  risk_types: RiskType[];
}

/**
 * 内容安全检测请求参数
 *
 * 参数示例:
 * ```typescript
 * {
 *   "uri": "https://example.com/image.jpg",
 *   "risk_types": ["POLITY", "EROTIC", "VIOLENT", "CHILD"],
 *   "detected_age": 0,
 *   "is_video": 0
 * }
 * ```
 */
export interface ScanParams {
  /**
   * 图片或视频的URL地址
   * 注意：视频检测必须使用 uri，不支持 img_base64
   */
  uri?: string;
  /**
   * 图片的Base64编码字符串
   * 注意：视频检测不支持 base64 输入
   */
  img_base64?: string;
  /**
   * 需要检测的风险类型列表
   * 可选值：POLITY（政治敏感）、EROTIC（色情内容）、VIOLENT（暴力血腥）、CHILD（儿童安全）
   */
  risk_types: RiskType[];
  /**
   * 是否进行年龄检测，0或1
   * @default 0
   */
  detected_age: number;
  /**
   * 是否为视频文件，0或1
   * @default 0
   */
  is_video: number;
}

/**
 * 内容安全检测响应结果
 */
export interface ScanResponse {
  /**
   * 请求是否成功
   */
  ok: boolean;
  /**
   * 风险级别，范围 0-6
   */
  nsfw_level?: number;
  /**
   * 检测到的标签列表
   */
  label_items?: LabelItem[];
  /**
   * 实际检测到的风险类型列表
   */
  risk_types?: RiskType[];
  /**
   * 年龄组信息 [年龄段代码, 置信度]
   */
  age_group?: any[];
  /**
   * 错误信息
   */
  error?: string;
  /**
   * 视频时长（秒）
   */
  video_duration?: number;
  /**
   * 最高风险帧的索引号
   */
  max_risk_frame?: number;
  /**
   * 总抽帧数量
   */
  frame_count?: number;
  /**
   * 实际检测的帧数
   */
  frames_checked?: number;
  /**
   * 是否因检测到6级风险而提前退出
   */
  early_exit?: boolean;
  /**
   * 每一帧的详细检测结果
   */
  frame_results?: FrameResult[];
}

/**
 * scan - 内容安全检测
 *
 * 云服务内容安全检测接口，支持图片和视频的风险检测。
 *
 * Features:
 * - 支持图片和视频检测
 * - 多种风险类型检测（政治敏感、色情、暴力、儿童安全）
 * - 风险级别评分（0-6）
 * - 视频逐帧检测
 * - 可选年龄检测
 *
 * @example
 * ```typescript
 * import { initSeacloud, scan } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * // 图片检测
 * const imageResult = await scan({
 *   uri: "https://example.com/image.jpg",
 *   risk_types: ["POLITY", "EROTIC", "VIOLENT", "CHILD"],
 *   detected_age: 0,
 *   is_video: 0
 * });
 *
 * console.log(imageResult.nsfw_level); // 风险级别 0-6
 * console.log(imageResult.label_items); // 检测到的标签
 *
 * // 视频检测
 * const videoResult = await scan({
 *   uri: "https://example.com/video.mp4",
 *   risk_types: ["EROTIC", "VIOLENT"],
 *   detected_age: 0,
 *   is_video: 1
 * });
 *
 * console.log(videoResult.frame_results); // 每一帧的检测结果
 * ```
 *
 * @param params 请求参数，详见 ScanParams 接口定义
 * @returns 检测结果，包含风险级别、标签等信息
 * @throws {SeacloudError} 当请求失败或参数不合法时抛出错误
 */
export async function scan(params: ScanParams): Promise<ScanResponse> {
  // 参数验证
  if (!params.uri && !params.img_base64) {
    throw new SeacloudError('必须提供 uri 或 img_base64 中的至少一个参数');
  }

  if (params.is_video === 1 && params.img_base64) {
    throw new SeacloudError('视频检测不支持 base64 输入，必须使用 uri 参数');
  }

  if (!params.risk_types || params.risk_types.length === 0) {
    throw new SeacloudError('必须提供至少一个风险类型');
  }

  const client = await getClient();
  const config = client.getConfig();

  // 复用现有的认证和请求逻辑
  const url = `${config.baseUrl}/scan`;
  const token = await getApiToken(config.apiKey);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.timeout);

  try {
    const response = await config.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(params),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorBody = await response.text();
      const err = new SeacloudError(
        `HTTP ${response.status}: ${errorBody}`,
        response.status,
        errorBody
      );
      checkAndNotifyInsufficientBalance(err);
      throw err;
    }

    const responseData = await response.json() as any;
    checkResponseBodyForInsufficientBalance(responseData);

    // 检查是否有包装格式 { success, data }
    let result: ScanResponse;
    if (responseData.success !== undefined && responseData.data) {
      result = responseData.data as ScanResponse;
    } else {
      result = responseData as ScanResponse;
    }

    if (!result.ok) {
      throw new SeacloudError(
        `内容安全检测业务错误: ${result.error || '未知错误'}`,
        undefined,
        result
      );
    }

    return result;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof SeacloudError) {
      throw error;
    }

    if ((error as Error).name === 'AbortError') {
      throw new SeacloudError(`Request timeout after ${config.timeout}ms`);
    }

    throw new SeacloudError(
      `Request failed: ${(error as Error).message}`,
      undefined,
      error
    );
  }
}
